/**
 * <predictive-search> — typeahead over Shopify's Predictive Search API,
 * rendered server-side via the Section Rendering API so the markup is the
 * predictive-search section, not a client template.
 *
 * Contract:
 * - Additive: the form is a plain GET to /search and stays fully working
 *   if this module never runs or any fetch fails.
 * - Debounced 250ms, in-flight requests aborted, minimum 2 characters.
 * - Combobox keyboard pattern: ArrowDown/Up move aria-activedescendant
 *   across role="option" links, Enter follows the active option (plain
 *   Enter submits the form), Escape closes the panel and returns focus
 *   to the summary — matching the nav panels' Escape behavior.
 * - Below 990px the panel is fixed full-screen; the drawer's trapFocus /
 *   removeTrapFocus utilities own focus while it is open.
 */
import { trapFocus, removeTrapFocus } from './global.js';

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.details = this.closest('details[data-header-search]');
    this.input = this.querySelector('[data-search-input]');
    this.results = this.querySelector('[data-search-results]');
    this.status = this.querySelector('[data-search-status]');
    this.form = this.querySelector('form');
    if (!this.details || !this.input || !this.results) return;

    this.summary = this.details.querySelector('summary');
    this.abort = null;
    this.timer = null;
    this.activeId = '';

    this.details.addEventListener('toggle', () => this.onToggle());
    this.input.addEventListener('input', () => this.onInput());
    this.input.addEventListener('keydown', (e) => this.onKeydown(e));
    this.querySelector('[data-search-close]')?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.details.open) {
        e.preventDefault();
        this.close();
      }
    });
    /* Click outside the panel closes it (desktop dropdown mode). */
    document.addEventListener('click', (e) => {
      if (this.details.open && !this.details.contains(e.target)) this.close();
    });
  }

  onToggle() {
    if (this.details.open) {
      if (window.matchMedia('(max-width: 989px)').matches) {
        trapFocus(this.details.querySelector('[data-search-panel]'), this.input);
        document.body.style.overflow = 'hidden';
      } else {
        this.input.focus();
      }
    } else {
      removeTrapFocus(this.summary);
      document.body.style.overflow = '';
      this.clearResults();
      this.input.value = '';
    }
  }

  close() {
    this.details.removeAttribute('open');
  }

  onInput() {
    clearTimeout(this.timer);
    const q = this.input.value.trim();
    if (q.length < MIN_CHARS) {
      this.clearResults();
      return;
    }
    this.timer = setTimeout(() => this.fetchResults(q), DEBOUNCE_MS);
  }

  async fetchResults(q) {
    this.abort?.abort();
    this.abort = new AbortController();
    const base = window.routes?.predictive_search_url || '/search/suggest';
    const params = new URLSearchParams({
      q,
      'resources[type]': 'product,collection,page,article',
      'resources[limit]': '4',
      section_id: 'predictive-search',
    });
    try {
      const res = await fetch(`${base}?${params}`, { signal: this.abort.signal });
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const body = doc.querySelector('.predictive__body');
      if (!body) return;
      this.results.innerHTML = '';
      this.results.appendChild(body);
      this.results.hidden = false;
      this.setActive('');
      this.input.setAttribute('aria-expanded', 'true');
      if (this.status) {
        const count = Number(body.dataset.resultCount || 0);
        this.status.textContent = count
          ? (window.shorelineStrings?.searchSuggestions || '[count] suggestions').replace('[count]', count)
          : (window.shorelineStrings?.searchNoSuggestions || 'No suggestions');
      }
    } catch (err) {
      if (err.name !== 'AbortError') this.clearResults();
    }
  }

  clearResults() {
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.setActive('');
    this.input.setAttribute('aria-expanded', 'false');
  }

  options() {
    return [...this.results.querySelectorAll('[role="option"]')];
  }

  setActive(id) {
    this.activeId = id;
    if (id) this.input.setAttribute('aria-activedescendant', id);
    else this.input.removeAttribute('aria-activedescendant');
    this.options().forEach((o) => o.classList.toggle('is-active', o.id === id));
  }

  onKeydown(e) {
    const opts = this.options();
    if (!opts.length && e.key !== 'Escape') return;
    const idx = opts.findIndex((o) => o.id === this.activeId);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = opts[(idx + 1) % opts.length];
      this.setActive(next.id);
      next.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = opts[(idx - 1 + opts.length) % opts.length];
      this.setActive(prev.id);
      prev.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && this.activeId) {
      e.preventDefault();
      const active = opts.find((o) => o.id === this.activeId);
      if (active) window.location.href = active.href;
    }
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
